import { Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import * as Leaflet from 'leaflet';
import 'leaflet.markercluster';
import { OperatorFunction, Observable, debounceTime, distinctUntilChanged, map, filter } from 'rxjs';
import { BookingService } from 'src/app/services/booking.service';
import { LeafletInfoDialogComponent } from '../../../dialogs/leaflet-info-dialog/leaflet-info-dialog.component';
import * as _ from 'lodash';
import { BookingLMApiService, CustomerPlantConfigurationModelExtLMApi, CustomerPlantModelExtLMApi } from 'src/app/api';
import { saveInLocalStorage, fillFieldFromLocalStorage } from 'src/app/services/utils';
import { TopBarService } from 'src/app/services/topbar.service';
import { PrivacyInitialDialogComponent } from 'src/app/dialogs/privacy-initial-dialog/privacy-initial-dialog.component';
Leaflet.Icon.Default.imagePath = 'assets/';

@Component({
  selector: 'app-book',
  templateUrl: './book.component.html',
})
export class BookComponent implements OnInit {
  loaded: boolean = false;

  map!: Leaflet.Map;
  markers: Leaflet.Marker[] = [];
  warehousesList: CustomerPlantModelExtLMApi[] | null = [];

  searchText: any;
  options = {
    layers: [
      Leaflet.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        //'https://maps.loadmanager.cloud/osm_tiles/{z}/{x}/{y}.png'
      ),
    ],
    //'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'),
    zoom: 16,
    center: { lat: 42.30155, lng: 12.34215 },
    worldCopyJump: true
  };

  // Marker cluster stuff
  markerClusterGroup: Leaflet.MarkerClusterGroup = new Leaflet.MarkerClusterGroup();
  markerClusterData: any[] = [];
  markerClusterOptions: Leaflet.MarkerClusterGroupOptions | undefined;

  now = new Date();

  constructor(
    private route: ActivatedRoute,
    private zone: NgZone,
    private modal: NgbModal,
    public bookingService: BookingService,
    private router: Router,
    private apiService: BookingLMApiService,
    private topBarService: TopBarService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((paramMap) => {
      let lang = paramMap.get('lang');

      //GESTIONE LINGUA
      let localStorageLang = localStorage.getItem('lang');
      if (localStorageLang) this.bookingService.lang = localStorageLang;
      else if (lang) this.bookingService.lang = lang;

      //GESTIRE PRIVACY
      let localStoragePrivacy = localStorage.getItem('load_manager_privacy_version');
      if (!localStoragePrivacy) {
        let privacyRef = this.modal.open(PrivacyInitialDialogComponent, { centered: true, backdrop: 'static', keyboard: false });
      }

      //GESTIONE STEPPER
      this.bookingService.setStepper([
        { label: '1', url: 'book' },
        { label: '2', url: 'book/new', desktopPhase: 0 },
        { label: '3', url: 'book/new', desktopPhase: 1 },
        { label: '4', url: 'book/new/slot' },
        { label: 'END', url: '' },
      ]);
      this.bookingService.setStepperActive(0);
      //SALVATAGGIO URL DI PARTENZA
      if (!sessionStorage['base_url'] || sessionStorage['base_url'] != location.href) {
        sessionStorage.setItem('base_url', location.href);
      }

      this.topBarService.resetTopBar();
      this.apiService.getWarehouses([]).subscribe((x: CustomerPlantModelExtLMApi[] | null) => {
        if (x) {
          let res: CustomerPlantModelExtLMApi[] = x;
          this.bookingService.warehousesList = res;
        } else this.bookingService.warehousesList = x;

        this.warehousesList = this.bookingService.warehousesList;
        this.initMarkers();
      });
    });
    this.loaded = true;
  }

  search: OperatorFunction<string, readonly (string | null | undefined)[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map((term) =>
        this.warehousesList
          ? this.warehousesList
              .map((x) => x.description)
              .filter((v) => (v ? v.toLowerCase().indexOf(term.toLowerCase()) > -1 : false))
              .slice(0, 10)
          : []
      )
    );
  selectSearch = (event: NgbTypeaheadSelectItemEvent<any>) => {
    let description = event.item;
    let warehouseSelected = this.warehousesList?.find((x) => x.description == description);
    if (warehouseSelected) this.map.setView(new Leaflet.LatLng(warehouseSelected.lat!, warehouseSelected.lng!), 13);
  };

  initMarkers() {
    const defaultIcon = Leaflet.icon({
      iconUrl: 'assets/hub.png',
      iconSize: [64, 37],
    });
    this.warehousesList?.map((data) => {
      const marker = Leaflet.marker({ lat: data.lat!, lng: data.lng! }, { icon: defaultIcon });
      marker.on('click', () => {
        this.zone.run(() => {
          this.map.setView(new Leaflet.LatLng(data.lat!, data.lng!), 13);
          this.showPopup(data);
        }); //error
      });
      this.markerClusterGroup.addLayer(marker);
    });
    this.map.attributionControl.setPrefix(false);
    this.map.addLayer(this.markerClusterGroup);

    navigator.permissions.query({ name: 'geolocation' }).then((result) => {
      console.log('geolocation: ' + result.state);
    });

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (loc) => {
          let coordinatesInLocalStorage = fillFieldFromLocalStorage('lastWarehouseSelected');
          //Posizionamento Mappa default: Se già selezionato un magazzino punto quello,
          //altrimenti controllo che ci sia qualcosa nel localstorage, altrimento mi geolocalizzo
          if (this.bookingService.selectedWarehouseData) {
            this.map.panTo({
              lat: this.bookingService.selectedWarehouseData.lat!,
              lng: this.bookingService.selectedWarehouseData.lng!,
            });
            this.map.setZoom(16);
          } else if (coordinatesInLocalStorage) {
            let json = JSON.parse(coordinatesInLocalStorage);
            this.map.panTo(json);
            this.map.setZoom(16);
          } else {
            this.map.panTo({ lat: loc.coords.latitude, lng: loc.coords.longitude });
            this.map.setZoom(16);
          }
        },
        (error) => {
          console.log(error);
          this.map.setZoom(6);
          this.map.panTo({ lat: 42.30155, lng: 12.34215 });
        },
        { maximumAge: Infinity, timeout: 2000, enableHighAccuracy: false }
      );
    }
  }
  markerClusterReady(group: Leaflet.MarkerClusterGroup) {
    this.markerClusterGroup = group;
  }
  onMapReady($event: Leaflet.Map) {
    this.map = $event;
    this.initMarkers();
  }

  showPopup(data: CustomerPlantModelExtLMApi): void {
    const leafletInfoDialogRef = this.modal.open(LeafletInfoDialogComponent, { centered: true });
    leafletInfoDialogRef.componentInstance.data = data;
    leafletInfoDialogRef.closed.subscribe((res: any) => {
      if (res) {
        this.bookingService.selectedWarehouseData = data;
        this.goNext();
      }
    });
  }
  goNext = () => {
    saveInLocalStorage('lastWarehouseSelected', JSON.stringify({ lat: this.bookingService.selectedWarehouseData?.lat, lng: this.bookingService.selectedWarehouseData?.lng }));
    // Chiamata per recuperare la configurazione della warehouse selezionata.
    if (this.bookingService.selectedWarehouseData?.token) {
      this.loaded = false;
      this.bookingService.resetModel();
      this.apiService.getWarehouseConfiguration(this.bookingService.selectedWarehouseData?.token).subscribe((x: CustomerPlantConfigurationModelExtLMApi) => {
        this.bookingService.warehouseConfiguration = x;
        this.bookingService.checkConfiguration(x);
        // RESETTARE TUTTI I CAMPI ATTUALMENTE INSERITI IN PRENOTAZIONE
        this.router.navigate(['new'], { relativeTo: this.route });
      });
    }
  };
}
